import { useTranslate } from 'module/hooks/use-translation';
import {
  faTriangleExclamation,
  faCheckCircle,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Dialog } from '@rocketlane/ui';
import styled from 'styled-components';

const SourceModalBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-05);
  margin: var(--spacing-01) 0;
`;

const SourceLine = styled.div<{ $error?: boolean }>`
  display: flex;
  align-items: center;
  gap: var(--spacing-02);
  color: ${(p) => (p.$error ? 'var(--error-color)' : 'var(--text-primary)')};
`;

const SourceError = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-03);
`;

export interface SourceErrorData {
  emailEngagementCount: number;
  meetingEngagementCount: number;
}

export interface SourceErrorModalsProps {
  noSourceOpen: boolean;
  incompleteOpen: boolean;
  sourceErrorData: SourceErrorData | null;
  hasEmailSourceForModal: boolean;
  hasMeetingSourceForModal: boolean;
  canRetry: boolean;
  isRetryPending: boolean;
  onClose: () => void;
  onConfirmCreate: () => void;
}

export function SourceErrorModals({
  noSourceOpen,
  incompleteOpen,
  sourceErrorData,
  hasEmailSourceForModal,
  hasMeetingSourceForModal,
  canRetry,
  isRetryPending,
  onClose,
  onConfirmCreate,
}: SourceErrorModalsProps) {
  const t = useTranslate();

  return (
    <>
      <Dialog
        open={noSourceOpen}
        onCancel={onClose}
        title={t('agent_selection.source_error.no_source_title')}
        footer={
          <Button variant="secondary" onClick={onClose}>
            {t('agent_selection.source_error.close')}
          </Button>
        }
        content={
          <SourceModalBody>
            {sourceErrorData && (
              <SourceError>
                {hasEmailSourceForModal &&
                  sourceErrorData.emailEngagementCount === 0 && (
                    <SourceLine $error>
                      <FontAwesomeIcon icon={faTriangleExclamation} />
                      {t('agent_selection.source_error.email_threads_linked', {
                        count: 0,
                      })}
                    </SourceLine>
                  )}
                {hasMeetingSourceForModal &&
                  sourceErrorData.meetingEngagementCount === 0 && (
                    <SourceLine $error>
                      <FontAwesomeIcon icon={faTriangleExclamation} />
                      {t(
                        'agent_selection.source_error.meeting_transcripts_linked',
                        {
                          count: 0,
                        },
                      )}
                    </SourceLine>
                  )}
              </SourceError>
            )}
            <p>{t('agent_selection.source_error.no_source_message')}</p>
          </SourceModalBody>
        }
      />

      <Dialog
        open={incompleteOpen}
        onCancel={onClose}
        title={t('agent_selection.source_error.incomplete_title')}
        footer={
          <div className="flex gap-2 justify-end">
            <Button variant="secondary" onClick={onClose}>
              {t('agent_selection.source_error.cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={onConfirmCreate}
              disabled={!canRetry || isRetryPending}
            >
              {t('agent_selection.source_error.create')}
            </Button>
          </div>
        }
        content={
          <SourceModalBody>
            <p style={{ margin: 0 }}>
              {t('agent_selection.source_error.incomplete_message')}
            </p>
            {sourceErrorData && (
              <SourceError>
                {hasEmailSourceForModal && (
                  <SourceLine
                    $error={sourceErrorData.emailEngagementCount === 0}
                  >
                    {sourceErrorData.emailEngagementCount === 0 ? (
                      <>
                        <FontAwesomeIcon icon={faTriangleExclamation} />
                        {t(
                          'agent_selection.source_error.email_threads_linked',
                          { count: 0 },
                        )}
                      </>
                    ) : (
                      <>
                        <FontAwesomeIcon
                          icon={faCheckCircle}
                          style={{ color: 'var(--success-color)' }}
                        />
                        {t(
                          'agent_selection.source_error.email_threads_linked',
                          {
                            count: sourceErrorData.emailEngagementCount,
                          },
                        )}
                      </>
                    )}
                  </SourceLine>
                )}
                {hasMeetingSourceForModal && (
                  <SourceLine
                    $error={sourceErrorData.meetingEngagementCount === 0}
                  >
                    {sourceErrorData.meetingEngagementCount === 0 ? (
                      <>
                        <FontAwesomeIcon icon={faTriangleExclamation} />
                        {t(
                          'agent_selection.source_error.meeting_transcripts_linked',
                          { count: 0 },
                        )}
                      </>
                    ) : (
                      <>
                        <FontAwesomeIcon
                          icon={faCheckCircle}
                          style={{ color: 'var(--success-color)' }}
                        />
                        {t(
                          'agent_selection.source_error.meeting_transcripts_linked',
                          {
                            count: sourceErrorData.meetingEngagementCount,
                          },
                        )}
                      </>
                    )}
                  </SourceLine>
                )}
              </SourceError>
            )}
          </SourceModalBody>
        }
      />
    </>
  );
}
